import { FormField, FormLabel, FormSelect, FormInput, Button } from '../ui';
import { ContactFilterOptions } from '../../types/contact';
import { Search, X } from 'lucide-react';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { sessionService } from '../../services';

interface ContactFiltersProps {
  filters: ContactFilterOptions;
  onFilterChange: (filters: ContactFilterOptions) => void;
  onResetFilters: () => void;
}

function ContactFilters({ filters, onFilterChange, onResetFilters }: ContactFiltersProps) {
  const [localFilters, setLocalFilters] = useState<ContactFilterOptions>({
    ...filters,
  });

  const { data: sessions } = useQuery({
    queryKey: ['sessions'],
    queryFn: () => sessionService.getSessions(),
  });

  const handleLocalFilterChange = (name: string, value: string) => {
    setLocalFilters(prev => ({
      ...prev,
      [name]: value || undefined,
    }));
  };

  const handleApplyFilters = () => {
    onFilterChange({
      ...localFilters,
      page: 1, // Reset to first page when filters change
    });
  };

  const hasActiveFilters = Object.keys(filters).some(
    key => key !== 'page' && key !== 'limit' && filters[key as keyof ContactFilterOptions]
  );

  return (
    <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow mb-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        <FormField>
          <FormLabel htmlFor="search">Busca Geral</FormLabel>
          <FormInput
            id="search"
            name="search"
            value={localFilters.search || ''}
            onChange={e => handleLocalFilterChange('search', e.target.value)}
            placeholder="Buscar por nome ou contato..."
          />
        </FormField>

        <FormField>
          <FormLabel htmlFor="name">Nome</FormLabel>
          <FormInput
            id="name"
            name="name"
            value={localFilters.name || ''}
            onChange={e => handleLocalFilterChange('name', e.target.value)}
            placeholder="Buscar por nome..."
          />
        </FormField>

        <FormField>
          <FormLabel htmlFor="contact">Contato</FormLabel>
          <FormInput
            id="contact"
            name="contact"
            value={localFilters.contact || ''}
            onChange={e => handleLocalFilterChange('contact', e.target.value)}
            placeholder="Buscar por contato..."
          />
        </FormField>

        <FormField>
          <FormLabel htmlFor="channel">Canal</FormLabel>
          <FormSelect
            id="channel"
            name="channel"
            value={localFilters.channel || ''}
            onChange={e => handleLocalFilterChange('channel', e.target.value)}
          >
            <option value="">Todos</option>
            <option value="whatsapp">WhatsApp</option>
            <option value="email">Email</option>
            <option value="instagram">Instagram</option>
            <option value="facebook">Facebook</option>
            <option value="sms">SMS</option>
            <option value="phone">Telefone</option>
            <option value="website">Website</option>
            <option value="telegram">Telegram</option>
            <option value="messenger">Messenger</option>
            <option value="api">API</option>
            <option value="none">Nenhum</option>
            <option value="other">Outro</option>
          </FormSelect>
        </FormField>

        <FormField>
          <FormLabel htmlFor="type">Tipo</FormLabel>
          <FormSelect
            id="type"
            name="type"
            value={localFilters.type || ''}
            onChange={e => handleLocalFilterChange('type', e.target.value)}
          >
            <option value="">Todos</option>
            <option value="administrative">Administrativo</option>
            <option value="registration">Cadastro</option>
            <option value="commercial">Comercial</option>
            <option value="financial">Financeiro</option>
            <option value="legal_representative">Representante Legal</option>
            <option value="technical">Técnico</option>
            <option value="other">Outro</option>
          </FormSelect>
        </FormField>

        <FormField>
          <FormLabel htmlFor="is_group">Grupo</FormLabel>
          <FormSelect
            id="is_group"
            name="is_group"
            value={localFilters.is_group || ''}
            onChange={e => handleLocalFilterChange('is_group', e.target.value)}
          >
            <option value="">Todos</option>
            <option value="true">Grupos</option>
            <option value="false">Individuais</option>
          </FormSelect>
        </FormField>

        <FormField>
          <FormLabel htmlFor="whatsapp_id">Sessão WhatsApp</FormLabel>
          <FormSelect
            id="whatsapp_id"
            name="whatsapp_id"
            value={localFilters.whatsapp_id || ''}
            onChange={e => handleLocalFilterChange('whatsapp_id', e.target.value)}
          >
            <option value="">Todas</option>
            {sessions?.map(session => (
              <option key={session.id} value={session.id}>
                {session.session_name}
              </option>
            ))}
          </FormSelect>
        </FormField>
      </div>

      <div className="flex justify-end mt-4 space-x-2">
        {hasActiveFilters && (
          <Button variant="secondary" onClick={onResetFilters} className="flex items-center">
            <X className="h-4 w-4 mr-2" />
            Limpar Filtros
          </Button>
        )}
        <Button onClick={handleApplyFilters} className="flex items-center">
          <Search className="h-4 w-4 mr-2" />
          Filtrar
        </Button>
      </div>
    </div>
  );
}

export default ContactFilters;
